import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useIntl, defineMessages } from 'react-intl';

import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import BreadcrumbStepPropType from '../definitions/BreadcrumbStepPropType';
import { isStepActive, isStepDisabled } from '../functions/getBreadcrumbStatus';
import BreadcrumbAccordion from './BreadcrumbAccordion';
import Breadcrumb from './Breadcrumb';


function BreadcrumbTrail({ steps, onBreadcrumbClick, ...otherProps }) {
    const intl = useIntl();

    return (
        <nav
            aria-label={intl.formatMessage(MESSAGES.NAV_LABEL)}
            {...otherProps}
        >
            <ul className="breadcrumb-trail-list">
                <For
                    each="step"
                    index="index"
                    of={steps}
                >
                    <li
                        key={step.id}
                        className={classNames('breadcrumb-trail-list-item', {
                            'show-blackberry-border': !isStepDisabled(step),
                        })}
                        aria-current={!step.steps && isStepActive(step)}
                    >
                        <Choose>
                            <When condition={step.steps?.length > 0}>
                                <BreadcrumbAccordion
                                    step={step}
                                    stepNumber={index + 1}
                                    onBreadcrumbClick={onBreadcrumbClick}
                                />
                            </When>
                            <Otherwise>
                                <Breadcrumb
                                    key={step.id}
                                    {...step}

                                    onClick={onBreadcrumbClick ? () => onBreadcrumbClick(step) : undefined}
                                    data-name={onBreadcrumbClick ? step.id : undefined}
                                
                                    descriptor={step.stepDescriptor}
                                    values={step.stepValues}
                                
                                    stepNumber={index + 1}
                                />
                            </Otherwise>
                        </Choose>
                    </li>
                </For>
            </ul>
        </nav>
    );
}

BreadcrumbTrail.propTypes = {
    steps: PropTypes.arrayOf(
        BreadcrumbStepPropType,
    ).isRequired,
    onBreadcrumbClick: PropTypes.func,
};

const StyledBreadcrumbTrail = styled(observer(BreadcrumbTrail))`
    > .breadcrumb-trail-list {
        list-style: none;
        padding: 0;
        margin: 0;

        > li.breadcrumb-trail-list-item {
            position: relative;
            margin-bottom: ${Sizes.SPACING.ONE};

            /* Min height required so space between only this level of breadcrumbs
                is larger, margin caused issues with inner tiers */
            min-height: 3.5rem;

            &::after {
                position: absolute;
                
                top: calc(${Sizes.ICONS.XS} + ${Sizes.SPACING.HALF} + 3px);
                bottom: calc(-${Sizes.ICONS.XS});
                left: calc((${Sizes.ICONS.XS} + ${Sizes.SPACING.ONE}) / 2);

                min-height: ${Sizes.SPACING.ONE_AND_A_HALF};

                content: ' ';

                border-left: 1px dashed ${Colours.STONE};
            }

            &.show-blackberry-border::after {
                border-left-color: ${Colours.BLACKBERRY};
            }

            &:last-of-type {
                margin-bottom: 0;

                &::after {
                    content: none;
                }
            }
        }
    }
`;


const MESSAGES = defineMessages({
    NAV_LABEL: {
        id: 'base-ui.breadcrumb-trail.nav-label',
        defaultMessage: 'Breadcrumbs',
    },
});


export default StyledBreadcrumbTrail;
